import classnames from 'classnames'
import * as React from 'react'

import { Button, Heading } from '../../../components'
import { ChipSwitch, ChipSwitchButton } from '../../../components/ChipSwitch'
import { RedGreenText } from '../../../components/RedGreenText'
import { Table, TableAlign, TableBody, TableCell, TableRow } from '../../../components/Table'
import { formatNumber } from '../../../util'
import { parseRouteInitData } from '../../../util-routing'
import { QuoteContext } from '../QuoteContext'

type InstitutionalOwnershipInitData = {
  managersOwnership: Array<InstitutionalOwnershipItem> | null
  fundsOwnership: Array<InstitutionalOwnershipItem> | null
}

interface InstitutionalOwnershipItem {
  investorId: string
  name: string
  percOwnership: number
}

enum InstitutionalOwnershipType {
  Managers,
  Funds,
}

function getInvestorLink(investorId: string, type: InstitutionalOwnershipType) {
  const view = type === InstitutionalOwnershipType.Funds ? 'fund-details' : 'manager-details'
  return `/insidertrading.ashx?view=${view}&investorId=${investorId}`
}

const isRedesignEnabled = FinvizSettings.hasRedesignEnabled

const commonTdClassName = {
  '!px-0.5': !isRedesignEnabled,
}

export function InstitutionalOwnership() {
  const { index } = React.useContext(QuoteContext)
  const { managersItems, hasManagersItems, fundsItems, hasFundsItems } = React.useMemo(() => {
    const initData = parseRouteInitData<InstitutionalOwnershipInitData>({
      elementId: `institutional-ownership-init-data-${index}`,
      removeElement: false,
    })

    const managersItems = initData?.managersOwnership ?? []
    const fundsItems = initData?.fundsOwnership ?? []

    return {
      managersItems,
      hasManagersItems: managersItems.length > 0,
      fundsItems,
      hasFundsItems: fundsItems.length > 0,
    }
  }, [index])

  const [institutionalOwnership, setInstitutionalOwnership] = React.useState(
    hasManagersItems ? InstitutionalOwnershipType.Managers : InstitutionalOwnershipType.Funds
  )
  const items = institutionalOwnership === InstitutionalOwnershipType.Managers ? managersItems : fundsItems

  return (
    <div
      className={classnames('h-full space-y-2', {
        'rounded-md border border-primary py-2': isRedesignEnabled,
      })}
    >
      <div
        className={classnames('flex w-full items-center justify-between', {
          'px-2': isRedesignEnabled,
          'px-0.5': !isRedesignEnabled,
        })}
      >
        <Heading
          level={4}
          className={classnames('m-0', {
            'text-2xs text-gray-400 font-normal': isRedesignEnabled,
            'text-3xs': !isRedesignEnabled,
          })}
        >
          Institutional Ownership
        </Heading>
        <ChipSwitch size="small" rounding={!isRedesignEnabled ? 'none' : undefined}>
          <ChipSwitchButton
            disabled={!hasManagersItems}
            active={institutionalOwnership === InstitutionalOwnershipType.Managers}
            onClick={() => {
              setInstitutionalOwnership(InstitutionalOwnershipType.Managers)
            }}
          >
            Managers
          </ChipSwitchButton>
          <ChipSwitchButton
            disabled={!hasFundsItems}
            active={institutionalOwnership === InstitutionalOwnershipType.Funds}
            onClick={() => {
              setInstitutionalOwnership(InstitutionalOwnershipType.Funds)
            }}
          >
            Funds
          </ChipSwitchButton>
        </ChipSwitch>
      </div>
      <div className="mx-2.5 h-0 border-t border-primary" />
      <Table
        className={classnames('styled-table-new table-fixed', {
          'px-0.5': isRedesignEnabled,
        })}
      >
        <TableBody>
          {items.map(({ investorId, name, percOwnership }) => (
            <TableRow key={investorId}>
              <TableCell hasPadding={false} className={classnames(commonTdClassName, 'truncate')}>
                <Button
                  as="a"
                  href={getInvestorLink(investorId, institutionalOwnership)}
                  theme="link"
                  align="left"
                  size="none"
                  className="inline border-none"
                  contentClass="truncate"
                >
                  {name ?? '-'}
                </Button>
              </TableCell>
              <TableCell align={TableAlign.Right} className={classnames(commonTdClassName, 'w-18')}>
                <RedGreenText value={percOwnership} rightContent={null}>
                  {formatNumber(percOwnership, { suffix: '%' })}
                </RedGreenText>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
